// components/CredentialsModal.jsx
import React, { useState } from 'react';
import { AlertTriangle, Check, Copy, KeyRound } from 'lucide-react';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from './ui/dialog';

/**
 * Les identifiants d'un compte, montrés une seule fois.
 *
 * Utilisée à la création d'un employé (NewEmployeeForm) et à l'approbation
 * d'une demande d'accès (Support).
 *
 * Elle ne se ferme ni par un clic à l'extérieur ni par Échap : ces identifiants
 * ne seront plus jamais affichés, un clic malheureux les perdrait. Seuls le
 * bouton du pied et la croix la ferment.
 *
 * Elle reposait sur Radix directement, avec son propre voile noir à 50 % et un
 * z-index de 9999 — hors des fenêtres de KLASYS. Elle passe sur les primitives
 * `Dialog` (DESIGN-SYSTEM.md), sans changer ce qu'elle fait.
 */
const CredentialsModal = ({ credentials, onClose, setToastInfo, typeDemande = 'creation' }) => {
  const [copied, setCopied] = useState({ username: false, password: false });
  const reinitialisation = typeDemande === 'reinitialisation';

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied((prev) => ({ ...prev, [field]: true }));
      setTimeout(() => setCopied((prev) => ({ ...prev, [field]: false })), 2000);
    }).catch((err) => {
      console.error('Copie impossible :', err);
    });
  };

  // `onClose(true)` : NewEmployeeForm s'en sert pour réinitialiser son formulaire.
  const fermer = () => {
    if (typeof onClose === 'function') {
      onClose(true);
      if (setToastInfo) {
        setToastInfo({ message: 'Employé créé avec succès', type: 'success' });
      }
    }
  };

  const Champ = ({ libelle, valeur, champ }) => (
    <div>
      <label className="mb-1.5 block text-xs font-medium text-gray-700">{libelle}</label>
      <div className="flex items-center gap-2">
        <input
          type="text"
          readOnly
          value={valeur || ''}
          className="h-9 flex-1 rounded-lg border border-gray-200 bg-gray-50 px-3 font-mono text-sm text-gray-900"
        />
        <Button
          type="button"
          variant="outline"
          onClick={() => copyToClipboard(valeur || '', champ)}
          className="h-9 w-[110px] justify-center gap-2"
          title={`Copier : ${libelle.toLowerCase()}`}
        >
          {copied[champ] ? <Check size={15} className="text-green-600" /> : <Copy size={15} />}
          {copied[champ] ? 'Copié' : 'Copier'}
        </Button>
      </div>
    </div>
  );

  return (
    <Dialog open onOpenChange={(ouvert) => { if (!ouvert) fermer(); }}>
      <DialogContent
        className="w-[95vw] max-w-5xl !max-h-[92vh] overflow-hidden flex flex-col p-0 gap-0"
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader className="flex-row items-center gap-3 space-y-0 border-b px-5 py-4 text-left">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-[#246990] text-white">
            <KeyRound size={18} />
          </div>
          <div className="min-w-0">
            <DialogTitle className="text-lg font-semibold text-gray-900">
              {reinitialisation ? 'Nouveau mot de passe généré' : 'Identifiants de connexion'}
            </DialogTitle>
            <DialogDescription className="text-sm text-gray-500">
              {reinitialisation
                ? "Le mot de passe a été réinitialisé. L'identifiant reste inchangé."
                : 'Transmettez-les à la personne concernée.'}
            </DialogDescription>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
              <AlertTriangle size={18} className="mt-0.5 shrink-0 text-amber-600" />
              <div>
                <p className="font-semibold">Notez-les maintenant</p>
                <p className="mt-1 text-amber-800">
                  Ils ne seront plus affichés. Le mot de passe est temporaire : son changement sera
                  demandé à la première connexion.
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <Champ
                libelle={reinitialisation ? 'Identifiant (inchangé)' : 'Identifiant'}
                valeur={credentials?.username}
                champ="username"
              />
              <Champ
                libelle={reinitialisation ? 'Nouveau mot de passe' : 'Mot de passe temporaire'}
                valeur={credentials?.password}
                champ="password"
              />
            </div>
          </div>
        </div>

        <DialogFooter className="border-t bg-gray-50 px-5 py-4">
          <Button
            type="button"
            onClick={fermer}
            className="h-9 min-w-[168px] justify-center gap-2 border border-transparent bg-[#246990] text-white hover:bg-[#1f5a7a]"
          >
            <Check size={16} />
            J'ai noté les identifiants
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CredentialsModal;
